/**
 * plugins/vuetify.ts
 *
 * Framework documentation: https://vuetifyjs.com`
 */

// Styles
import 'vuetify/styles';

// Composables
import * as directives from 'vuetify/directives';
import { aliases, fa } from 'vuetify/iconsets/fa-svg';
import { de, en, fr, it } from 'vuetify/locale';
import { config, themes } from '@deepcloud/deep-ui-lib';
import { fa as iconsDeepUiLib } from '@deepcloud/deep-ui-lib/config/icons';
import colors from 'vuetify/util/colors';

function convertFaIconsToSet(icons: Record<string, string>, set = 'far') {
  return Object.fromEntries(
    Object.entries(icons).map(([key, value]) => [
      key,
      value.startsWith('fa') ? value.replace(/^fa\w*/, set) : value // Only replace fa* with specified set
    ])
  );
}

// https://vuetifyjs.com/en/introduction/why-vuetify/#feature-guides
export default config.createVuetifyConfig({
  directives,
  locale: {
    messages: {
      de,
      en,
      fr,
      it
    },
    locale: 'de'
  },
  theme: {
    defaultTheme: 'light',
    variations: {
      colors: ['primary', 'secondary'],
      lighten: 2,
      darken: 0
    },
    themes: {
      light: {
        ...themes.deepSignLight,
        colors: {
          ...themes.deepSignLight.colors,
          grey: colors.grey.base,
          'grey-darken-1': colors.grey.darken1,
          'grey-darken-2': colors.grey.darken2,
          'grey-darken-3': colors.grey.darken3,
          'grey-darken-4': colors.grey.darken4,
          'grey-lighten-1': colors.grey.lighten1,
          'grey-lighten-2': colors.grey.lighten2,
          'grey-lighten-3': colors.grey.lighten3,
          'grey-lighten-4': colors.grey.lighten4,
          'grey-lighten-5': colors.grey.lighten5
        }
      },
      dark: {
        ...themes.deepSignDark
      }
    }
  },
  icons: {
    defaultSet: 'fa',
    aliases: {
      ...convertFaIconsToSet(aliases as Record<string, string>),
      ...convertFaIconsToSet(iconsDeepUiLib),
      ...{
        cancel: 'fat fa-xmark',
        expand: 'fal fa-chevron-down'
      }
    },
    sets: {
      fa
    }
  },
  display: {
    thresholds: {
      md: 960,
      lg: 1280,
      xl: 1920,
      xxl: 2560
    }
  },
  defaults: {
    // Vuetify Components
    VIcon: {
      size: 16
    },
    VAppBar: {
      flat: true,
      border: true
    },
    VMenu: {
      transition: 'fade-transition',
      VCard: {
        class: 'rounded-lg'
      },
      VList: {
        rounded: 'lg'
      }
    },
    VTextField: {
      variant: 'outlined',
      color: 'primary',
      density: 'compact',
      autocomplete: 'off'
    },
    VNavigationDrawer: {
      flat: true,
      border: true
    },
    VTextarea: {
      variant: 'outlined',
      color: 'primary',
      rounded: 'lg',
      autocomplete: 'off'
    },
    VSelect: {
      variant: 'outlined',
      color: 'primary',
      itemColor: 'primary',
      density: 'compact'
    },
    VSheet: {
      rounded: 'md'
    },
    VAutocomplete: {
      variant: 'outlined',
      color: 'primary',
      autocomplete: 'off',
      itemColor: 'primary',
      menuProps: {
        contentClass: 'rounded-lg'
      }
    },
    VCombobox: {
      color: 'primary',
      itemColor: 'primary',
      autocomplete: 'off',
      menuProps: {
        contentClass: 'rounded-lg'
      }
    },
    VSwitch: {
      color: 'primary',
      inset: true
    },
    VDivider: {
      color: 'grey-lighten-2'
    },
    VDataTableServer: {
      VSelectionControl: {
        color: 'primary'
      }
    },
    VCheckbox: {
      color: 'primary'
    },
    VCheckboxBtn: {
      color: 'primary'
    },
    VChip: {
      variant: 'tonal'
    }
  }
});
